import { useCallback } from "react";
import { setUserId, setUserProperties } from "firebase/analytics";
import { analytics, logEvent } from "../services/firebase";
import { User } from "../services/api";

type ParamValue = string | number | boolean | null | undefined;

export interface AnalyticsEventParams {
  [key: string]: ParamValue;
}

export interface UseAppAnalyticsReturn {
  trackEvent: (
    eventName: string,
    params?: AnalyticsEventParams,
  ) => Promise<void>;
  identifyUser: () => Promise<void>;
  resetUser: () => void;
}

// Sem e-mail: o GA4 não recebe PII (termos do Google Analytics), e aqui o e-mail
// é de paciente. O `user_id` é o id interno, que só o banco resolve.
export const useAppAnalytics = ({
  user,
}: {
  user: User | null;
}): UseAppAnalyticsReturn => {
  const identifyUser = useCallback(async (): Promise<void> => {
    if (!analytics) return;

    const userId = user?.id ? String(user.id) : null;

    if (userId) {
      setUserId(analytics, userId);
    }

    // Apaga o e-mail que as versões até 10/2026 gravavam.
    setUserProperties(analytics, {
      user_email: null,
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetUser = useCallback((): void => {
    if (!analytics) return;
    setUserId(analytics, null as unknown as string);
    setUserProperties(analytics, {
      user_email: null,
    });
  }, []);

  const trackEvent = useCallback(
    async (
      eventName: string,
      params: AnalyticsEventParams = {},
    ): Promise<void> => {
      const enrichedParams: AnalyticsEventParams = {
        user_id: user?.id ?? undefined,
        client_timestamp: new Date().toISOString(),
        ...params,
      };

      logEvent(analytics, eventName, enrichedParams);
    },
    [user?.id], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return { trackEvent, identifyUser, resetUser };
};
